import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { motion } from 'framer-motion';
import Layout from './components/layout/Layout';

// Auth components
import ProtectedRoute from './components/auth/ProtectedRoute';

// Pages
import HomePage from './pages/HomePage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import DashboardPage from './pages/DashboardPage';
import BookListing from './pages/BookListing';
import BookDetailPage from './pages/BookDetailPage';
import NotFoundPage from './pages/NotFoundPage';
import AboutPage from './pages/AboutPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import WriteReviewPage from './pages/WriteReviewPage';
import HowToPage from './pages/HowToPage';

// Admin Pages
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import ManageBooksPage from './pages/admin/ManageBooksPage';
import ManageUsersPage from './pages/admin/ManageUsersPage';

// Teacher Pages
import TeacherDashboardPage from './pages/TeacherDashboardPage';

// Page Transition Animation
const pageVariants = {
  initial: {
    opacity: 0,
    y: 20
  },
  in: {
    opacity: 1,
    y: 0
  },
  out: {
    opacity: 0,
    y: -20
  }
};

const pageTransition = {
  type: 'tween',
  ease: 'anticipate',
  duration: 0.3
};

const PageLayout = ({ children }) => (
  <motion.div
    initial="initial"
    animate="in"
    exit="out"
    variants={pageVariants}
    transition={pageTransition}
    className="min-h-screen"
  >
    {children}
  </motion.div>
);

function App() {
  return (
    <AuthProvider>
      <Router>
        <Layout>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<PageLayout><HomePage /></PageLayout>} />
            <Route path="/login" element={<PageLayout><LoginPage /></PageLayout>} />
            <Route path="/register" element={<PageLayout><RegisterPage /></PageLayout>} />
            <Route path="/forgot-password" element={<PageLayout><ForgotPasswordPage /></PageLayout>} />
            <Route path="/about" element={<PageLayout><AboutPage /></PageLayout>} />
            <Route path="/privacy-policy" element={<PageLayout><PrivacyPolicyPage /></PageLayout>} />
            <Route path="/howto" element={<PageLayout><HowToPage /></PageLayout>} />
            
            {/* Protected Routes */}
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <PageLayout><DashboardPage /></PageLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/books" 
              element={
                <ProtectedRoute>
                  <PageLayout><BookListing /></PageLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/books/:bookId" 
              element={
                <ProtectedRoute>
                  <PageLayout><BookDetailPage /></PageLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/books/:bookId/review" 
              element={
                <ProtectedRoute>
                  <PageLayout><WriteReviewPage /></PageLayout>
                </ProtectedRoute>
              } 
            />
            
            {/* Admin Routes */}
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <PageLayout><AdminDashboardPage /></PageLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/books" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <PageLayout><ManageBooksPage /></PageLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/users" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <PageLayout><ManageUsersPage /></PageLayout>
                </ProtectedRoute>
              } 
            />
            
            {/* Teacher Routes */}
            <Route 
              path="/teacher/dashboard" 
              element={
                <ProtectedRoute requiredRole="teacher">
                  <PageLayout><TeacherDashboardPage /></PageLayout>
                </ProtectedRoute>
              } 
            />
            
            {/* 404 Route */}
            <Route path="*" element={<PageLayout><NotFoundPage /></PageLayout>} />
          </Routes>
        </Layout>
      </Router>
    </AuthProvider>
  );
}

export default App;
